/**
 * Plan C Outline Editor Modal
 * 
 * Simplified outline editor for Plan C jobs.
 * Uses Plan C's GET/PUT /jobs/{job_id}/outline endpoints.
 * 
 * AUTH: Uses apiFetch from lib/apiFetch.js (NOT custom getToken)
 * RETRY: 520/network errors are retried with exponential backoff
 * SAVE CONTRACT: Blocking overlay during save, pipeline resume on success
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Save, X, ChevronRight, AlertCircle, RefreshCw } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

// Save states for blocking overlay
const SAVE_STATE = {
  IDLE: 'IDLE',
  SAVING_OUTLINE: 'SAVING_OUTLINE',
  RESUMING_PIPELINE: 'RESUMING_PIPELINE',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR'
};

const PlanCOutlineEditorModal = ({ 
  isOpen, 
  onClose, 
  jobId, 
  onSaveAndContinue 
}) => {
  const [loading, setLoading] = useState(false);
  const [saveState, setSaveState] = useState(SAVE_STATE.IDLE);
  const [error, setError] = useState(null);
  const [outline, setOutline] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastFetchedJobId, setLastFetchedJobId] = useState(null);

  // P0 DEBUG: Log prop changes
  useEffect(() => {
    console.log('[PlanCOutlineModal] PROPS', { jobId, isOpen });
  }, [jobId, isOpen]);

  /**
   * Fetch outline from API
   */
  const fetchOutline = useCallback(async (targetJobId) => {
    if (!targetJobId) return;
    
    setLoading(true);
    setError(null);
    
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.error('[PlanCOutlineModal] GET outline BLOCKED - no accessToken');
      setError('Not authenticated. Please log in again.');
      setLoading(false);
      return;
    }

    const url = `${API}/publisher/plan-c/jobs/${targetJobId}/outline`;
    console.log('[PlanCOutlineModal] GET outline START', { url, jobId: targetJobId });

    const result = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }, { maxRetries: 2, backoff: [1000, 2000, 4000] });

    console.log('[PlanCOutlineModal] GET outline RESPONSE', { 
      ok: result.ok,
      status: result.status,
      is_backend_unavailable: result.is_backend_unavailable
    });

    if (!result.ok) {
      const errorMsg = result.json?.detail || result.error?.message || `Failed to fetch outline: ${result.status}`;
      console.error('[PlanCOutlineModal] GET outline FAILED', { error: errorMsg });
      setError(errorMsg);
      setLoading(false);
      return;
    }

    console.log('[PlanCOutlineModal] GET outline SUCCESS', { 
      jobId: targetJobId,
      nodeCount: result.json?.nodes?.length,
      playbackOrderLength: result.json?.playback_order?.length,
      authoritative: result.json?.authoritative
    });
    
    setOutline(result.json);
    setLoading(false);
    setLastFetchedJobId(targetJobId);
  }, []);

  // Fetch outline when modal opens or jobId changes
  useEffect(() => {
    if (isOpen && jobId && jobId !== lastFetchedJobId) {
      console.log('[PlanCOutlineModal] TRIGGER_FETCH', { isOpen, jobId, lastFetchedJobId });
      fetchOutline(jobId);
    }
  }, [isOpen, jobId, lastFetchedJobId, fetchOutline]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen && lastFetchedJobId) {
      // These resets are intentional when modal closes
      setLastFetchedJobId(null);
      setOutline(null);
      setError(null);
      setSaveState(SAVE_STATE.IDLE);
    }
  }, [isOpen, lastFetchedJobId]);

  /**
   * Retry fetch on error
   */
  const handleRetryFetch = () => {
    setLastFetchedJobId(null);
    fetchOutline(jobId);
  };

  /**
   * Save outline and resume pipeline
   * CONTRACT:
   * 1. Disable modal close
   * 2. Show blocking overlay
   * 3. PUT /outline with retry
   * 4. On success, POST /run with retry
   * 5. Close modal only after /run acknowledged
   */
  const handleSave = async () => {
    if (!outline) {
      console.warn('[PlanCOutlineModal] PUT outline BLOCKED - no outline data');
      return;
    }
    
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.error('[PlanCOutlineModal] PUT outline BLOCKED - no accessToken');
      setError('Not authenticated. Please log in again.');
      return;
    }

    // Step 1: Save outline to R2
    setSaveState(SAVE_STATE.SAVING_OUTLINE);
    setError(null);

    const payload = {
      artifact_version: outline.artifact_version,
      nodes: outline.nodes,
      playback_order: outline.playback_order
    };

    const putUrl = `${API}/publisher/plan-c/jobs/${jobId}/outline`;
    console.log('[PlanCOutlineModal] PUT outline START', { 
      url: putUrl, 
      jobId,
      nodeCount: payload.nodes?.length
    });

    const putResult = await apiFetch(putUrl, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }, { maxRetries: 3, backoff: [1000, 2000, 4000] });

    console.log('[PlanCOutlineModal] PUT outline RESPONSE', {
      ok: putResult.ok,
      status: putResult.status,
      is_backend_unavailable: putResult.is_backend_unavailable
    });

    if (!putResult.ok) {
      const errorMsg = putResult.json?.detail || putResult.error?.message || `Failed to save outline: ${putResult.status}`;
      console.error('[PlanCOutlineModal] PUT outline FAILED', { error: errorMsg });
      setSaveState(SAVE_STATE.ERROR);
      setError(errorMsg);
      return;
    }

    console.log('[PlanCOutlineModal] PUT outline SUCCESS', { 
      jobId,
      canonical_outline_r2_key: putResult.json?.canonical_outline_r2_key
    });

    // Step 2: Resume pipeline
    setSaveState(SAVE_STATE.RESUMING_PIPELINE);

    const runUrl = `${API}/publisher/plan-c/jobs/${jobId}/run`;
    console.log('[PlanCOutlineModal] POST run START', { url: runUrl, jobId });

    const runResult = await apiFetch(runUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }, { maxRetries: 3, backoff: [1000, 2000, 4000] });

    console.log('[PlanCOutlineModal] POST run RESPONSE', {
      ok: runResult.ok,
      status: runResult.status,
      is_backend_unavailable: runResult.is_backend_unavailable
    });

    // 200/202 are both success for /run (idempotent endpoint)
    if (!runResult.ok && runResult.status !== 202) {
      const errorMsg = runResult.json?.detail || runResult.error?.message || `Failed to resume pipeline: ${runResult.status}`;
      console.error('[PlanCOutlineModal] POST run FAILED', { error: errorMsg });
      setSaveState(SAVE_STATE.ERROR);
      setError(`Outline saved, but pipeline resume failed: ${errorMsg}`);
      return;
    }

    console.log('[PlanCOutlineModal] POST run SUCCESS - pipeline resuming', {
      jobId,
      phase: runResult.json?.phase,
      state: runResult.json?.state
    });

    // Step 3: Success - notify parent and close
    setSaveState(SAVE_STATE.SUCCESS);
    setHasChanges(false);

    if (onSaveAndContinue) {
      console.log('[PlanCOutlineModal] CALLING onSaveAndContinue - modal will close');
      onSaveAndContinue(putResult.json);
    }
  };

  /**
   * Retry save after error
   */
  const handleRetrySave = () => {
    setSaveState(SAVE_STATE.IDLE);
    setError(null);
    handleSave();
  };

  /**
   * Move node up/down
   */
  const moveNode = (index, direction) => {
    if (!outline?.nodes) return;
    
    const newNodes = [...outline.nodes];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= newNodes.length) return;
    
    [newNodes[index], newNodes[newIndex]] = [newNodes[newIndex], newNodes[index]];
    
    newNodes.forEach((node, i) => {
      node.order = i;
    });
    
    const newPlaybackOrder = newNodes
      .filter(n => n.type === 'SEGMENT')
      .map(n => n.segment_id);
    
    setOutline(prev => ({
      ...prev,
      nodes: newNodes,
      playback_order: newPlaybackOrder
    }));
    setHasChanges(true);
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Blocking overlay during save (cannot close modal)
  const isSaving = saveState === SAVE_STATE.SAVING_OUTLINE || saveState === SAVE_STATE.RESUMING_PIPELINE;
  const isInitialLoad = !outline && !error && (loading || !lastFetchedJobId);

  return (
    <Dialog open={isOpen} onOpenChange={isSaving ? undefined : onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-slate-900 border-slate-700 text-slate-50 flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Plan C Outline Editor</span>
            {outline?.authoritative && (
              <span className="text-xs bg-green-600 px-2 py-1 rounded">Canonical</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* BLOCKING OVERLAY during save */}
        {isSaving && (
          <div className="absolute inset-0 bg-slate-900/90 z-50 flex flex-col items-center justify-center rounded-lg">
            <Loader2 className="w-12 h-12 animate-spin text-cyan-400 mb-4" />
            <p className="text-lg font-semibold text-white mb-2">
              {saveState === SAVE_STATE.SAVING_OUTLINE 
                ? 'Saving canonical outline to durable storage…' 
                : 'Resuming pipeline…'}
            </p>
            <p className="text-sm text-slate-400">Please do not close this window.</p>
          </div>
        )}

        <div className="flex-1 overflow-hidden flex flex-col">
          {isInitialLoad ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              <span className="ml-3 text-slate-400">Loading outline...</span>
            </div>
          ) : error && saveState !== SAVE_STATE.ERROR ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-400 mb-4">{error}</p>
                <Button onClick={handleRetryFetch} variant="outline" className="border-slate-600">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          ) : saveState === SAVE_STATE.ERROR ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-400 mb-4">{error}</p>
                <p className="text-sm text-slate-400 mb-4">Your outline changes have not been lost.</p>
                <Button onClick={handleRetrySave} className="bg-cyan-600 hover:bg-cyan-500">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Save
                </Button>
              </div>
            </div>
          ) : outline ? (
            <>
              {/* Outline Info */}
              <div className="mb-4 p-3 bg-slate-800 rounded text-sm">
                <div className="flex gap-4">
                  <span className="text-slate-400">Job: <span className="text-slate-200">{jobId}</span></span>
                  <span className="text-slate-400">Version: <span className="text-slate-200">v{outline.artifact_version}</span></span>
                  <span className="text-slate-400">Segments: <span className="text-slate-200">{outline.nodes?.length || 0}</span></span>
                </div>
              </div>

              {/* Outline Nodes */}
              <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                {outline.nodes?.map((node, index) => (
                  <div 
                    key={node.node_id || index}
                    className="flex items-center gap-2 p-3 bg-slate-800 rounded border border-slate-700 hover:border-slate-600"
                  >
                    <div className="flex flex-col gap-1">
                      <button 
                        onClick={() => moveNode(index, -1)}
                        disabled={index === 0}
                        className="p-1 hover:bg-slate-700 rounded disabled:opacity-30"
                      >
                        <ChevronRight className="w-4 h-4 rotate-[-90deg]" />
                      </button>
                      <button 
                        onClick={() => moveNode(index, 1)}
                        disabled={index === outline.nodes.length - 1}
                        className="p-1 hover:bg-slate-700 rounded disabled:opacity-30"
                      >
                        <ChevronRight className="w-4 h-4 rotate-90" />
                      </button>
                    </div>

                    <div className="w-8 h-8 flex items-center justify-center bg-slate-700 rounded text-sm font-mono">
                      {index + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {node.title || `Segment ${index + 1}`}
                      </div>
                      <div className="text-xs text-slate-400 truncate">
                        {node.segment_id} • {node.type}
                      </div>
                      {node.text && (
                        <div className="text-xs text-slate-500 mt-1 truncate">
                          {node.text.substring(0, 100)}...
                        </div>
                      )}
                    </div>

                    {node.linear !== undefined && (
                      <span className={`text-xs px-2 py-1 rounded ${node.linear ? 'bg-green-900/50 text-green-400' : 'bg-yellow-900/50 text-yellow-400'}`}>
                        {node.linear ? 'Linear' : 'Non-linear'}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {outline.playback_order && (
                <div className="mt-4 p-3 bg-slate-800/50 rounded text-xs">
                  <span className="text-slate-400">Playback Order:</span>
                  <span className="ml-2 text-slate-300 font-mono">
                    {outline.playback_order.length} segments
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              No outline data available
            </div>
          )}
        </div>

        {/* Actions - disabled during save */}
        <div className="flex justify-between gap-3 pt-4 border-t border-slate-700">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="border-slate-600 text-slate-300"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={isSaving || isInitialLoad || !outline}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="plan-c-outline-save-btn"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {hasChanges ? 'Save & Continue' : 'Confirm & Continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanCOutlineEditorModal;
